import React from "react";
import { useSelector } from "react-redux";
import { createBusinessDomain } from "../../../helpers";
import { useToast } from "../../../provider/ToastProvider";
import { userState } from "../../../redux/reducers/userReducer";
import axiosInstance from "../../../utils/axiosInstance";
import { CURRENT_ENV, getUrlForBookings, getUrlForClasses } from "../../../utils/confighelpers";
import { Card, FcDrawer, Icon } from "../schedule/ui";
import { RadioCards } from "./MemberActionModals";
import { apiResult, euro, whatsappNumber } from "./memberUtils";

/**
 * Sell a pass or subscription to one member (new design).
 *  - Payment link (default): POST bookings/createCheckoutSessionsOfUserPurchase — the same Stripe Checkout the
 *    FitClub apps use, created on this member's own Stripe customer; the webhook adds the product to this member
 *    once paid. Subscriptions then renew automatically. The link is valid for 24 hours.
 *    The club gets a short link (2026-11-01): <club address>/p/<code> (page ShortPayLinkPage opens Stripe), or Stripe's
 *    own long address when no short code came back.
 *  - Invoice: POST bookings/purchaseClassPass {generatePaymentLink:true} (adds the product now, draft invoice),
 *    then sendStripeInvoiceEmail or (one-time products only) markAsPayStripeInvoice — same as the old dialog.
 */

/** Short payment link on the club's own address (same address as the trial link), this site's address when running locally */
const shortPayLink = (code, userData) => {
  const club = userData?.myBookingLink;
  const local = CURRENT_ENV === "local" || window.location.hostname === "localhost";
  return `${club && !local ? createBusinessDomain(club) : `${window.location.origin}/`}p/${code}`;
};

const periodLabel = (p) => {
  const bp = p?.productFields?.billingPeriod;
  if (!bp?.interval) return "";
  const n = parseInt(bp.interval_count, 10) || 1;
  return n === 1 ? ` / ${bp.interval}` : ` / ${n} ${bp.interval}s`;
};

export default function SellProductDrawer({ open, onClose, consumer, onDone = () => null, productType = "" }) {
  // productType "classpass" | "subscription" limits the list (member page: Add pass / Add subscription); empty = both.
  const kinds = productType ? [productType] : ["classpass", "subscription"];
  const noun = productType === "subscription" ? "subscription" : productType === "classpass" ? "pass" : "pass or subscription";
  const { addSuccessToast, addErrorToast } = useToast();
  const userData = useSelector(userState).userData;
  const [products, setProducts] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [productId, setProductId] = React.useState("");
  const [way, setWay] = React.useState("link");
  const [step, setStep] = React.useState("form"); // form | link | invoice
  const [busy, setBusy] = React.useState("");
  const [error, setError] = React.useState("");
  const [link, setLink] = React.useState("");
  const [purchase, setPurchase] = React.useState(null);
  const first = (consumer?.fullName || "").split(" ")[0] || "them";

  React.useEffect(() => {
    if (!open) return;
    setStep("form");
    setWay("link");
    setError("");
    setBusy("");
    setLink("");
    setPurchase(null);
    setLoading(true);
    (async () => {
      try {
        const res = await axiosInstance({ method: "get", url: `${getUrlForClasses("")}products/list?getall=false` });
        const [ok, data] = apiResult(res);
        const list = ok && Array.isArray(data) ? data.filter((p) => parseFloat(p.price) > 0 && kinds.includes(p.productType)) : [];
        setProducts(list);
        setProductId(list[0]?._id || "");
      } catch (e) {
        setProducts([]);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, productType]);

  const product = products.find((p) => p._id === productId);
  const isSub = product?.productType === "subscription";

  const createLink = async () => {
    const res = await axiosInstance({
      method: "post",
      url: `${getUrlForBookings("")}createCheckoutSessionsOfUserPurchase`,
      data: {
        productId: product._id,
        username: consumer?.username,
        fullName: consumer?.fullName,
        email: consumer?.email || "",
        productAddedBy: "business",
        generatePaymentLink: true,
        success_url: `${window.location.origin}/pay/done`,
        cancel_url: `${window.location.origin}/pay/cancelled`,
      },
    });
    const [ok, data, msg] = apiResult(res);
    const url = data?.shortCode ? shortPayLink(data.shortCode, userData) : data?.checkoutSession?.url || data?.paymentLink;
    if (ok && url) {
      setLink(url);
      setStep("link");
    } else setError(msg || "Couldn't create a payment link.");
  };

  const createInvoice = async () => {
    const res = await axiosInstance({
      method: "post",
      url: `${getUrlForBookings()}purchaseClassPass`,
      data: {
        productId: product._id,
        migrationCredit: 1,
        username: consumer?.username || "",
        fullName: consumer?.fullName || "",
        email: consumer?.email || "",
        isPaid: false,
        paymentMethod: "NA",
        paymentMode: "NA",
        paymentStatus: "Unpaid",
        generatePaymentLink: true,
      },
    });
    const [ok, data, msg] = apiResult(res);
    if (!ok) return setError(msg || "Couldn't add this product.");
    const info = data?.purchasingInfo || {};
    onDone();
    if (info?.stripeMetadata?.stripeinvoiceId) {
      setPurchase(info);
      setStep("invoice");
    } else {
      addSuccessToast(`${product.productName} added for ${first}. Stripe sends the first invoice when it's due.`);
      onClose();
    }
  };

  const submit = async () => {
    if (!product) return setError("Pick a product.");
    if (!consumer?.username) return setError("This member's details are still loading.");
    setError("");
    setBusy("submit");
    try {
      if (way === "link") await createLink();
      else await createInvoice();
    } catch (e) {
      setError("Couldn't reach the server. Please try again.");
    } finally {
      setBusy("");
    }
  };

  const invoiceAction = async (kind) => {
    setBusy(kind);
    setError("");
    try {
      const res = await axiosInstance({ method: "post", url: `${getUrlForBookings()}${kind === "send" ? "sendStripeInvoiceEmail" : "markAsPayStripeInvoice"}`, data: { purchase_id: purchase._id } });
      const [ok, , msg] = apiResult(res);
      if (ok) {
        addSuccessToast(kind === "send" ? `Invoice sent to ${consumer?.email}` : "Marked as paid · receipt sent");
        onDone();
        onClose();
      } else setError(msg || "That didn't work. Please try again.");
    } catch (e) {
      setError("Couldn't reach the server. Please try again.");
    } finally {
      setBusy("");
    }
  };

  const message = `Hi ${first}, here is your payment link for ${product?.productName || "your purchase"}: ${link}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      addSuccessToast("Payment link copied");
    } catch (e) {
      addErrorToast("Couldn't copy. Select the link and copy it yourself.");
    }
  };
  const wa = whatsappNumber(consumer);

  const options = products.map((p) => ({
    value: p._id,
    label: p.productName,
    sub:
      p.productType === "subscription"
        ? `Subscription${p.productFields?.unlimitedCredits ? " · unlimited credits" : p.productFields?.noOfAllowedSessions ? ` · ${p.productFields.noOfAllowedSessions} credits per period` : ""}`
        : `Credit pass · ${p.productFields?.noOfAllowedSessions || "?"} credits${p.productFields?.validUntilConsumed ? " · valid until used" : p.productFields?.validityDays ? ` · valid ${p.productFields.validityDays} days` : ""}`,
    right: `${euro(p.price)}${p.productType === "subscription" ? periodLabel(p) : ""}`,
  }));

  return (
    <FcDrawer
      open={open}
      onClose={() => !busy && onClose()}
      eyebrow={`FOR ${first.toUpperCase()}`}
      title={`Add a ${noun}`}
      subtitle="Send a payment link, or an invoice if they need one."
      primaryLabel={way === "link" ? "Create payment link" : `Add ${product?.productName || "product"}`}
      onPrimary={submit}
      busy={busy === "submit"}
      primaryDisabled={!product}
      hideFooter={step !== "form"}
    >
      {error && <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" role="alert">{error}</div>}

      {step === "form" && (
        <>
          <Card title="Product">
            {loading ? (
              <div className="flex flex-col gap-2">{[0, 1, 2].map((i) => <div key={i} className="h-16 animate-pulse rounded-2xl bg-gray-100" />)}</div>
            ) : options.length ? (
              <RadioCards label="Product" options={options} value={productId} onChange={setProductId} />
            ) : (
              <p className="rounded-2xl bg-[#FFF4E5] px-4 py-3 text-sm font-semibold text-[#7A4A00]">{productType === "subscription" ? "You have no subscriptions yet." : productType === "classpass" ? "You have no paid credit passes yet." : "You have no paid passes or subscriptions yet."} Create one under My Products first.</p>
            )}
          </Card>
          <Card title={`How will ${first} pay?`}>
            <RadioCards
              label="Payment"
              value={way}
              onChange={setWay}
              options={[
                {
                  value: "link",
                  label: "Send a payment link",
                  tag: <span className="rounded-full bg-[#E8F7EE] px-2 py-0.5 text-[11px] font-extrabold text-[#1F6B3A]">Recommended</span>,
                  sub: isSub ? "They pay by card or iDEAL. The subscription then renews automatically." : "They pay online by card or iDEAL. The pass is added once they have paid.",
                },
                {
                  value: "invoice",
                  label: "Send an invoice",
                  sub: isSub ? "Stripe emails an invoice every period. They pay each one within a day." : "Added right away. Stripe emails an invoice, or you mark it paid at the studio.",
                },
              ]}
            />
          </Card>
        </>
      )}

      {step === "link" && (
        <div className="flex flex-col items-center gap-3 px-2 py-6 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-3xl bg-[#E8F7EE] text-[#1F6B3A]">
            <Icon className="h-8 w-8" d={<path d="m5 12 5 5L20 7" />} />
          </span>
          <h3 className="text-xl font-black text-gray-900">Payment link ready</h3>
          <p className="max-w-md text-[15px] text-gray-600">
            Send it to {first}. {product?.productName} is added to their account as soon as they pay. The link works for 24 hours.
          </p>
          <div className="mt-2 flex w-full max-w-md gap-2">
            <input readOnly aria-label="Payment link" value={link} onFocus={(e) => e.target.select()} className="block h-12 w-full min-w-0 rounded-2xl border border-gray-200 bg-[#FBF9FC] px-4 text-sm text-gray-700 outline-none" />
            <button type="button" onClick={copy} className="shrink-0 rounded-2xl border border-gray-200 bg-white px-4 text-sm font-extrabold text-gray-900 hover:bg-gray-50">Copy</button>
          </div>
          <div className="mt-2 flex w-full max-w-md flex-col gap-2">
            <a href={`https://wa.me/${wa}?text=${encodeURIComponent(message)}`} target="_blank" rel="noopener noreferrer" className="flex h-12 items-center justify-center rounded-2xl bg-brown-coffee text-base font-extrabold text-white hover:bg-light-brown-coffee">
              Send on WhatsApp
            </a>
            {consumer?.email && (
              <a
                href={`mailto:${consumer.email}?subject=${encodeURIComponent(`Payment link for ${product?.productName || "your purchase"}`)}&body=${encodeURIComponent(message)}`}
                className="flex h-12 items-center justify-center rounded-2xl border border-gray-200 bg-white text-base font-extrabold text-gray-900 hover:bg-gray-50"
              >
                Email it to {consumer.email}
              </a>
            )}
            <button type="button" onClick={onClose} className="h-11 rounded-xl text-sm font-bold text-gray-600 hover:bg-gray-100">Done</button>
          </div>
        </div>
      )}

      {step === "invoice" && (
        <div className="flex flex-col items-center gap-3 px-2 py-6 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-3xl bg-[#E8F7EE] text-[#1F6B3A]">
            <Icon className="h-8 w-8" d={<path d="m5 12 5 5L20 7" />} />
          </span>
          <h3 className="text-xl font-black text-gray-900">{product?.productName} added for {first}</h3>
          <p className="max-w-md text-[15px] text-gray-600">Send the invoice now, or later from the Invoices tab. It goes to {consumer?.email}.</p>
          <div className="mt-2 flex w-full max-w-md flex-col gap-2">
            <button type="button" disabled={!!busy} onClick={() => invoiceAction("send")} className="flex h-12 items-center justify-center gap-2 rounded-2xl bg-brown-coffee text-base font-extrabold text-white hover:bg-light-brown-coffee disabled:opacity-60">
              {busy === "send" && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
              Send invoice with payment link
            </button>
            {!isSub && (
              <button type="button" disabled={!!busy} onClick={() => invoiceAction("paid")} className="flex h-12 items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-white text-base font-extrabold text-gray-900 hover:bg-gray-50 disabled:opacity-60">
                {busy === "paid" && <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700" />}
                Paid at the studio – send receipt
              </button>
            )}
            <button type="button" disabled={!!busy} onClick={onClose} className="h-11 rounded-xl text-sm font-bold text-gray-600 hover:bg-gray-100">Send later</button>
          </div>
        </div>
      )}
    </FcDrawer>
  );
}
